import React, { useMemo } from "react";
import { Mic } from "lucide-react";
import { deviationOf, KIND_LABEL, Timestamp } from "./KeyMoments.jsx";

// A turn shorter than this, or with fewer words, is not read. Speaker
// boundaries are routinely a word or two out, so "this is the" or "do It
// depends" under the rep's name is usually the BUYER's voice -- and on a
// man/woman call that reads as the rep's pitch "dropping 40%". Same rule as
// ECHO key_moments.MIN_VOICE_TURN_S / MIN_VOICE_TURN_WORDS; applied here too so
// cards scored before it stop charting those fragments.
const MIN_TURN_S = 3;
const MIN_TURN_WORDS = 4;

function readable(p) {
  const words = (p.said || "").trim().split(/\s+/).filter(Boolean).length;
  const long = p.end_seconds == null || p.end_seconds - p.at_seconds >= MIN_TURN_S;
  return long && (!p.said || words >= MIN_TURN_WORDS);
}
import { stamp } from "./CallPlayer.jsx";
import { voiceWords } from "./voiceWords.js";

// How your voice held up — and where it didn't.
//
// This replaced a line chart ("Did you hold steady?") that plotted pitch
// against an unlabelled higher / steady / lower axis. On a typical call it drew
// three dots and the sentence "Steady on 3 of 3 of your turns", and when it did
// catch something the reader still had to hover a dot to learn what was said
// there. It showed a measurement and left the meaning to the reader.
//
// What a rep can act on is narrower and more useful:
//
//   1. A verdict in one sentence: did your voice hold, and if not, was it under
//      pressure (right after a push-back, price, a hard question)? That is the
//      coachable case: two reps can say the same words to an objection and one
//      of them sounds certain.
//   2. A ribbon of the call: every turn you spoke, coloured by whether your
//      voice sat at your own normal, with the buyer's pressure moments marked
//      above it. Click a turn to hear it.
//   3. The few turns where it actually moved, each with what you said, what
//      the buyer had just done, and a play button.
//
// Every reading is relative to the rep's OWN baseline in the same call, never
// raw Hz. Absolute pitch is confounded by sex and voice type, and this renders
// against named people.

const NOTABLE = 0.12;            // below this a change is not audible as one
const PRESSURE_WINDOW_S = 25;    // how soon after a buyer moment a turn counts as "under pressure"
const PRESSURE_KINDS = new Set(["objection", "price", "buyer_question", "disengagement"]);
const MAX_LISTED = 4;

const MUTED = "var(--c-500)";
const RED = "var(--red-600, #d03b3b)";
const BLUE = "var(--blue-600)";
const STEADY = "var(--c-300, #9aa3b2)";
const AMBER = "var(--amber-600, #c98500)";

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };

function capitalise(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

function Title({ w }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: ".04em", color: "var(--c-600)" }}>
      <Mic size={13} /> How {w.your} voice held up
    </div>
  );
}

function Ribbon({ turns, pressure, duration, callLink, onSeek, w }) {
  const W = 600, H = 34, top = 12, barH = 14;
  const x = (t) => (Math.min(Math.max(t, 0), duration) / duration) * W;
  const colour = (t) => (!t.off ? STEADY : t.up ? RED : BLUE);

  return (
    <div style={{ marginTop: 10 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
           role="img" aria-label={`${w.Your} turns across the call, coloured by how ${w.your} voice sat against ${w.your} normal`}>
        <rect x={0} y={top} width={W} height={barH} rx={3} fill="var(--c-100, rgba(120,120,120,.1))" />
        {turns.map((t, i) => {
          const x0 = x(t.at);
          const w = Math.max(3, x(t.end ?? t.at + 2) - x0);
          return (
            <rect key={i} x={x0} y={top} width={w} height={barH} rx={2} fill={colour(t)}
                  style={{ cursor: onSeek ? "pointer" : "default" }}
                  {...(onSeek ? {
                    role: "button",
                    tabIndex: 0,
                    "aria-label": `Jump to ${stamp(t.at)}`,
                    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSeek(t.at); } },
                  } : {})}
                  onClick={() => onSeek && onSeek(t.at)}>
              <title>{`${stamp(t.at)} · ${t.reading || "measured"}${t.said ? `\n“${t.said}”` : ""}`}</title>
            </rect>
          );
        })}
        {pressure.map((p, i) => {
          const px = x(p.at_seconds);
          return (
            <path key={`p${i}`} d={`M${px - 4},1 L${px + 4},1 L${px},9 Z`} fill={AMBER}>
              <title>{`${stamp(p.at_seconds)} · ${KIND_LABEL[p.kind] || p.kind}`}</title>
            </path>
          );
        })}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: MUTED,
                    fontVariantNumeric: "tabular-nums" }}>
        <span>0:00</span><span>{stamp(duration)}</span>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: MUTED, marginTop: 2 }}>
        {[[STEADY, `${w.your} normal`], [RED, "higher or louder"], [BLUE, "lower or quieter"]].map(([c, label]) => (
          <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 8, borderRadius: 2, background: c, display: "inline-block" }} />
            {label}
          </span>
        ))}
        {pressure.length > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <svg width="9" height="8" aria-hidden="true"><path d="M0,0 L9,0 L4.5,8 Z" fill={AMBER} /></svg>
            buyer pushed back, raised price or asked
          </span>
        )}
        {onSeek && callLink && <span>· click a turn to hear it</span>}
      </div>
    </div>
  );
}

export default function VoiceTimeline({ call, onSeek, isSelf = false }) {
  const w = voiceWords(isSelf);
  const analysis = useMemo(() => {
    const moments = call?.key_moments || [];
    const pressure = moments
      .filter((m) => m.who !== "rep" && PRESSURE_KINDS.has(m.kind) && m.at_seconds != null)
      .sort((a, b) => a.at_seconds - b.at_seconds);

    // One reading per rep turn. Rows scored before turn-by-turn tracking fall
    // back to the rep's own key moments, which is fewer readings but the same
    // measure against the same baseline.
    let turns = (call?.voice_track || [])
      .filter((p) => p.pitch_vs_baseline != null || p.loudness_vs_baseline != null)
      .filter(readable)
      .map((p) => ({ at: p.at_seconds, end: p.end_seconds, reading: p.reading, said: p.said,
                     dev: deviationOf(p) }));
    const legacy = turns.length === 0;
    if (legacy) {
      turns = moments
        .filter((m) => m.who === "rep" && m.voice && m.at_seconds != null)
        .map((m) => ({ at: m.at_seconds, end: null, reading: m.voice.reading, said: m.said || m.title,
                       dev: deviationOf(m.voice) }));
    }
    turns.sort((a, b) => a.at - b.at);

    turns.forEach((t) => {
      t.off = Math.abs(t.dev) >= NOTABLE;
      t.up = t.dev > 0;
      // The buyer moment this turn answered, if it came right after one.
      t.trigger = [...pressure].reverse()
        .find((p) => p.at_seconds <= t.at + 1 && t.at - p.at_seconds <= PRESSURE_WINDOW_S) || null;
    });

    const off = turns.filter((t) => t.off);
    const lastSeen = Math.max(1, ...turns.map((t) => t.end ?? t.at), ...pressure.map((p) => p.at_seconds));
    return {
      turns, pressure, off, legacy,
      underPressure: off.filter((t) => t.trigger),
      duration: Math.max(Number(call?.duration_seconds) || 0, lastSeen),
    };
  }, [call?.voice_track, call?.key_moments, call?.duration_seconds]);

  const { turns, pressure, off, underPressure, duration } = analysis;

  if (!turns.length) {
    // Two different reasons produce nothing to show, and they must not share a
    // message: "no audio" is false when the recording WAS analysed and the
    // rep's own voice just couldn't be separated out.
    return (
      <div style={card}>
        <Title w={w} />
        <p style={{ color: MUTED, fontSize: 12.5, margin: "6px 0 0" }}>
          {call?.scored_with?.had_audio
            ? `The recording was analysed, but ${w.your} own turns couldn't be separated clearly enough to read ${w.your} voice.`
            : `No recording was available for this call, so ${w.your} voice couldn't be measured.`}
        </p>
      </div>
    );
  }

  const n = turns.length;
  const listed = [...off].sort((a, b) => Math.abs(b.dev) - Math.abs(a.dev)).slice(0, MAX_LISTED)
    .sort((a, b) => a.at - b.at);

  let verdict;
  if (!off.length) {
    verdict = n === 1
      ? `${w.Your} voice sat at ${w.your} own normal on the one turn long enough to measure. Nothing to work on here.`
      : `${w.Your} voice held steady on all ${n} of ${w.your} turns. Nothing to work on here.`;
  } else {
    verdict = `${w.Your} voice moved away from ${w.your} normal on ${off.length} of ${n} turns`;
    verdict += underPressure.length
      ? `, ${underPressure.length === off.length ? "every one of them" : `${underPressure.length} of them`} right after the buyer pushed back, raised price or asked something hard.`
      : ".";
  }

  // Only advise when the movement is tied to pressure. A voice that lifts on
  // an ordinary sentence may just be energy; one that climbs every time the
  // buyer pushes is the thing worth drilling.
  let advice = null;
  if (underPressure.length) {
    const climbs = underPressure.filter((t) => t.up).length >= underPressure.length / 2;
    advice = climbs
      ? `Under pressure ${w.your} voice climbs. Next time: take one beat of silence before answering, then say it at a normal pitch.`
      : `Under pressure ${w.your} voice drops away. Next time: keep the volume all the way to the end of the sentence.`;
  }

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10,
                    flexWrap: "wrap" }}>
        <Title w={w} />
        <span style={{ fontSize: 11, color: MUTED }}>
          compared with {w.your} own normal on this call · {n} turn{n === 1 ? "" : "s"} measured
        </span>
      </div>

      <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.5,
                  color: off.length ? "var(--c-900)" : "var(--green-600)", fontWeight: 600 }}>
        {verdict}
      </p>
      {advice && (
        <p style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "var(--c-700)" }}>{advice}</p>
      )}
      {!off.length && n < 3 && (
        <p style={{ margin: "4px 0 0", fontSize: 11.5, color: MUTED }}>
          {n === 1 ? "Only one turn was" : `Only ${n} turns were`} long enough to read, so this is a short sample.
        </p>
      )}

      {n >= 2 && (
        <Ribbon turns={turns} pressure={pressure} duration={duration} callLink={call?.call_link} onSeek={onSeek} w={w} />
      )}

      {listed.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10.5, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em" }}>
            Where it moved{off.length > listed.length ? ` (the ${listed.length} biggest)` : ""}
          </div>
          {listed.map((t, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "58px 1fr", gap: 10,
                                  padding: "8px 0", borderTop: i ? "1px solid var(--c-200)" : "none" }}>
              <div style={{ paddingTop: 1 }}>
                <Timestamp atSeconds={t.at} label={stamp(t.at)} callLink={call?.call_link} onSeek={onSeek} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 650, color: t.up ? RED : BLUE }}>
                  {capitalise(t.reading) || (t.up ? `Higher than ${w.your} normal` : `Lower than ${w.your} normal`)}
                </div>
                {t.said && (
                  <div style={{ marginTop: 2, fontSize: 12.5, color: "var(--c-800)", fontStyle: "italic" }}>
                    {w.You} said “{t.said}”
                  </div>
                )}
                {t.trigger && (
                  <div style={{ marginTop: 2, fontSize: 11.5, color: MUTED }}>
                    Right after: {(KIND_LABEL[t.trigger.kind] || t.trigger.kind).toLowerCase()}
                    {" "}at {stamp(t.trigger.at_seconds)}
                    {t.trigger.said ? ` — “${t.trigger.said.length > 90 ? `${t.trigger.said.slice(0, 87)}…` : t.trigger.said}”` : ""}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
